import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  CalendarOptionMenus,
  CalendarOptions,
  LocaleKey,
} from '@taopix/taopix-core/dist/types/types';
import {Button, ContentPanel, MinusIcon, PlusIcon, SelectList} from '@taopix/taopix-design-system';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {DateDropDown} from './CalendarPalette';

export type CalendarSettingsProps = {
  listItemData: any;
  onApplySettingsChange?: (data: Partial<CalendarOptions>) => void;
  onChangeSettings?: (data: Partial<CalendarOptions>) => void;
  calendarOptions: CalendarOptionMenus;
  calendarSettings: CalendarOptions;
  showApplyButtonInPanel?: boolean;
  canChangeNumberOfMonths?: boolean;
};

const CalendarSettings = React.forwardRef(
  (
    {
      listItemData,
      onApplySettingsChange,
      calendarOptions,
      calendarSettings,
      showApplyButtonInPanel = true,
      canChangeNumberOfMonths,
      onChangeSettings,
      ...props
    }: CalendarSettingsProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const ui = useUiState();
    const {t} = useTranslation(['calendars']);

    const [selectedLocale, setSelectedLocale] = useState<string>(calendarSettings?.locale);
    const [selectedStartDay, setSelectedStartDay] = useState<number>(calendarSettings?.startDay);

    const [selectedStartDate, setSelectedStartDate] = useState<DateDropDown>({
      month: calendarSettings?.startMonth,
      year: calendarSettings?.startYear,
    });

    const [selectedNumberOfMonths, setSelectedNumberOfMonths] = useState<number>(
      calendarSettings?.numberOfMonths as number
    );

    const [startYearIndex, setStartYearIndex] = useState<number>(-1);

    useEffect(() => {
      setStartYearIndex(
        listItemData?.years.findIndex(y => y.value === String(selectedStartDate.year))
      );
    }, [listItemData, selectedStartDate.year]);

    return (
      <div className={'flex-1 flex flex-col pb-lg space-y-sm'}>
        <div className={'flex-1 space-y-sm overflow-y-auto'}>
          {calendarSettings.startYearCanChange || calendarSettings.startMonthCanChange ? (
            <ContentPanel heading={t('*:str_LabelStartDate')} className={'space-y-sm'}>
              <SelectList
                id={'start-month-select'}
                items={listItemData?.months}
                selectedIndex={listItemData?.months.findIndex(
                  m => m.value === String(selectedStartDate.month)
                )}
                labelledBy={'StartMonth'}
                size={'small'}
                onChange={(event: any) => {
                  setSelectedStartDate({
                    ...selectedStartDate,
                    month: Number(event.target.value),
                  });
                  if (onChangeSettings) {
                    onChangeSettings({startMonth: Number(event.target.value)});
                  }
                }}
                displayMode={ui.state.isSmallScreen ? 'modal' : 'positioned'}
                disabled={!calendarSettings.startMonthCanChange}
                modalHeading={t('calendars:str_LabelStartMonth')}
              />
              <SelectList
                id={'start-years-select'}
                items={listItemData?.years}
                selectedIndex={startYearIndex}
                labelledBy={'StartYear'}
                size={'small'}
                disabled={startYearIndex === -1 || !calendarSettings.startYearCanChange}
                placeholder={startYearIndex === -1 ? `${selectedStartDate.year}` : undefined}
                onChange={(event: any) => {
                  setSelectedStartDate({
                    ...selectedStartDate,
                    year: Number(event.target.value),
                  });
                  if (onChangeSettings) {
                    onChangeSettings({startYear: Number(event.target.value)});
                  }
                }}
                displayMode={ui.state.isSmallScreen ? 'modal' : 'positioned'}
                modalHeading={t('calendars:str_LabelStartYear')}
              />
            </ContentPanel>
          ) : (
            <></>
          )}

          {calendarSettings.startDayCanChange ? (
            <ContentPanel heading={t('calendars:str_LabelStartDay')} className="space-y-sm">
              <SelectList
                items={listItemData?.days}
                selectedIndex={listItemData?.days.findIndex(
                  l => l.value === String(selectedStartDay)
                )}
                labelledBy={'start day'}
                size={'small'}
                onChange={(event: any) => {
                  setSelectedStartDay(Number(event.target.value));
                  if (onChangeSettings) {
                    onChangeSettings({startDay: Number(event.target.value)});
                  }
                }}
                title={t('*:str_LabelFirstDayOfTheWeek')}
                displayMode={ui.state.isSmallScreen ? 'modal' : 'positioned'}
              />
            </ContentPanel>
          ) : (
            <></>
          )}

          {canChangeNumberOfMonths ? (
            <ContentPanel heading={t('*:str_LabelMonthNumber')} className={'space-y-sm'}>
              {/* This feature only appears for products where the Calendar Mode is set to 'Use Page Styles' */}
              <div className={'flex space-x-md justify-start items-center'}>
                <div className={'mr-sm'}>
                  <>{selectedNumberOfMonths}</>
                </div>
                <Button
                  label={t('calendars:str_LabelReduceNumberOfMonths')}
                  hideLabel
                  size={'small'}
                  startIcon={<MinusIcon />}
                  buttonStyle={'distinct'}
                  disabled={
                    selectedNumberOfMonths === calendarOptions?.numberOfMonths?.min ||
                    selectedNumberOfMonths - calendarOptions?.numberOfMonths?.insert <
                      calendarOptions?.numberOfMonths?.min
                  }
                  onClick={() => {
                    setSelectedNumberOfMonths(
                      selectedNumberOfMonths - calendarOptions?.numberOfMonths?.insert
                    );
                    if (onChangeSettings) {
                      onChangeSettings({
                        numberOfMonths:
                          selectedNumberOfMonths - calendarOptions?.numberOfMonths?.insert,
                      });
                    }
                  }}
                />
                <Button
                  label={t('calendars:str_LabelIncreaseNumberOfMonths')}
                  hideLabel
                  size={'small'}
                  startIcon={<PlusIcon />}
                  buttonStyle={'distinct'}
                  disabled={
                    selectedNumberOfMonths === calendarOptions?.numberOfMonths?.max ||
                    selectedNumberOfMonths + calendarOptions?.numberOfMonths?.insert >
                      calendarOptions?.numberOfMonths?.max
                  }
                  onClick={() => {
                    setSelectedNumberOfMonths(
                      selectedNumberOfMonths + calendarOptions?.numberOfMonths?.insert
                    );
                    if (onChangeSettings) {
                      onChangeSettings({
                        numberOfMonths:
                          selectedNumberOfMonths + calendarOptions?.numberOfMonths?.insert,
                      });
                    }
                  }}
                />
              </div>
            </ContentPanel>
          ) : (
            <></>
          )}

          {calendarSettings.localeCanChange ? (
            <ContentPanel heading={t('*:str_LabelCalendarLanguage')} className={'space-y-sm'}>
              <SelectList
                items={listItemData?.locales}
                selectedIndex={listItemData?.locales.findIndex(
                  l => l.value === String(selectedLocale)
                )}
                labelledBy={'language'}
                size={'small'}
                onChange={(event: any) => {
                  setSelectedLocale(event.target.value);
                  if (onChangeSettings) {
                    onChangeSettings({locale: event.target.value as LocaleKey});
                  }
                }}
                displayMode={ui.state.isSmallScreen ? 'modal' : 'positioned'}
              />
            </ContentPanel>
          ) : (
            <></>
          )}
        </div>

        {showApplyButtonInPanel && (
          <Button
            label={t('*:str_ButtonApply')}
            className={'w-full mt-auto'}
            onClick={() => {
              if (onApplySettingsChange) {
                onApplySettingsChange({
                  startMonth: selectedStartDate?.month,
                  startYear: selectedStartDate?.year,
                  startDay: selectedStartDay,
                  numberOfMonths: selectedNumberOfMonths,
                  locale: selectedLocale as LocaleKey,
                });
              }
            }}
          />
        )}
      </div>
    );
  }
);

export default CalendarSettings;
